// src/Pages/ListingDetail.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  watchAvailabilityRange,
  ymd,
  addMonths,
  createOrGetInquiryThread,
  db,
} from "../firebase";
import {
  collection,
  query,
  orderBy,
  limit,
  getDocs,
  where,
  startAt,
  endAt,
  documentId,
  doc,
  getDoc,
  onSnapshot,
} from "firebase/firestore";

/* =========================================================================
   AMENITIES (Prefer CreateListing library exports; fall back locally)
   NOTE: FIXED — do NOT reference non-existent icon exports.
   ========================================================================= */
import * as amenitiesLib from "../lib/amenities";

const LIB_AMENITY_CATALOG = amenitiesLib?.AMENITY_CATALOG;
const LIB_AMENITY_ORDER = amenitiesLib?.AMENITY_ORDER;
const libNormalizeAmenityKey = amenitiesLib?.normalizeAmenityKey;
const libToAmenityList = amenitiesLib?.toAmenityList;

// FIX: no icon exports exist in ../lib/amenities — do not attempt to read them.
// If you add icons later, you can wire them here safely.
const LIB_AMENITY_ICONS = null;

const LOCAL_AMENITY_CATALOG = {
  wifi: { label: "Wi-Fi" },
  kitchen: { label: "Kitchen" },
  tv: { label: "TV" },
  washer: { label: "Washer" },
  dryer: { label: "Dryer" },
  ac: { label: "Air conditioning" },
  heating: { label: "Heating" },
  workspace: { label: "Dedicated workspace" },
  pool: { label: "Pool" },
  "hot tub": { label: "Hot tub" },
  "free parking": { label: "Free parking" },
  "ev charger": { label: "EV charger" },
  crib: { label: "Crib" },
  "hair dryer": { label: "Hair dryer" },
  iron: { label: "Iron" },
  essentials: { label: "Essentials" },
  "smoke alarm": { label: "Smoke alarm" },
  "carbon monoxide alarm": { label: "Carbon monoxide alarm" },
  "first aid kit": { label: "First aid kit" },
  "fire extinguisher": { label: "Fire extinguisher" },
  "private entrance": { label: "Private entrance" },
  backyard: { label: "Backyard" },
  "bbq grill": { label: "BBQ grill" },
  breakfast: { label: "Breakfast" },
  "pets allowed": { label: "Pets allowed" },
  "long term stays allowed": { label: "Long-term stays allowed" },
  "self check-in": { label: "Self check-in" },
  keypad: { label: "Keypad" },
};

const LOCAL_AMENITY_ORDER = [
  "wifi",
  "kitchen",
  "tv",
  "washer",
  "dryer",
  "ac",
  "heating",
  "workspace",
  "pool",
  "hot tub",
  "free parking",
  "ev charger",
  "private entrance",
  "backyard",
  "bbq grill",
  "breakfast",
  "pets allowed",
  "long term stays allowed",
  "self check-in",
  "keypad",
  "crib",
  "hair dryer",
  "iron",
  "essentials",
  "smoke alarm",
  "carbon monoxide alarm",
  "first aid kit",
  "fire extinguisher",
];

const AMENITY_CATALOG =
  (LIB_AMENITY_CATALOG && Object.keys(LIB_AMENITY_CATALOG).length
    ? LIB_AMENITY_CATALOG
    : LOCAL_AMENITY_CATALOG) || LOCAL_AMENITY_CATALOG;

const AMENITY_ORDER =
  (Array.isArray(LIB_AMENITY_ORDER) && LIB_AMENITY_ORDER.length
    ? LIB_AMENITY_ORDER
    : LOCAL_AMENITY_ORDER) || LOCAL_AMENITY_ORDER;

const AMENITY_SYNONYMS = new Map([
  ["wi-fi", "wifi"],
  ["wireless internet", "wifi"],
  ["air conditioning", "ac"],
  ["a/c", "ac"],
  ["television", "tv"],
  ["washing machine", "washer"],
  ["clothes dryer", "dryer"],
  ["desk", "workspace"],
  ["swimming pool", "pool"],
  ["jacuzzi", "hot tub"],
  ["spa", "hot tub"],
  ["parking", "free parking"],
  ["ev charging", "ev charger"],
  ["smoke detector", "smoke alarm"],
  ["co detector", "carbon monoxide alarm"],
  ["pack n play", "crib"],
  ["barbecue", "bbq grill"],
  ["pet friendly", "pets allowed"],
  ["long stay", "long term stays allowed"],
  ["smart lock", "keypad"],
]);

function localNormalizeAmenityKey(input) {
  const s = String(
    typeof input === "string" ? input : input?.key || input?.label || ""
  )
    .trim()
    .toLowerCase();
  if (!s) return "";

  let k = s.replace(/[_\-]/g, " ").replace(/\s+/g, " ").trim();
  if (AMENITY_SYNONYMS.has(k)) return AMENITY_SYNONYMS.get(k);

  const contains = (t) => k.includes(t);
  if (contains("wi fi") || contains("wifi") || contains("wireless")) return "wifi";
  if (contains("air") && (contains("cond") || contains("a/c"))) return "ac";
  if (contains("parking")) return "free parking";
  if (contains("ev") && contains("charg")) return "ev charger";
  if (contains("work") || contains("desk")) return "workspace";
  if (contains("swim") || contains("pool")) return "pool";
  if (contains("hot tub") || contains("jacuzzi") || contains("spa")) return "hot tub";
  if (contains("smoke") && (contains("alarm") || contains("detect"))) return "smoke alarm";
  if ((contains("carbon") && contains("monoxide")) || contains("co detect"))
    return "carbon monoxide alarm";
  if (contains("first aid")) return "first aid kit";
  if (contains("fire ext")) return "fire extinguisher";
  if (contains("long") && contains("stay")) return "long term stays allowed";
  if (contains("self") && contains("check")) return "self check-in";
  return k;
}

function localToAmenityList(src) {
  const out = [];
  const seen = new Set();

  const pushKey = (k) => {
    const key = localNormalizeAmenityKey(k);
    if (!key || seen.has(key)) return;
    seen.add(key);
    const label =
      AMENITY_CATALOG?.[key]?.label ||
      key
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    out.push({ key, label });
  };

  const walk = (val, depth = 0, seenObj = new WeakSet()) => {
    if (val == null || depth > 6) return;

    if (typeof val === "string") {
      val
        .split(/[,;\n]/g)
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach(pushKey);
      return;
    }

    if (Array.isArray(val)) {
      for (const item of val) {
        if (typeof item === "string") pushKey(item);
        else if (item && typeof item === "object") {
          if ("key" in item || "label" in item) pushKey(item.key || item.label);
          else walk(item, depth + 1, seenObj);
        }
      }
      return;
    }

    if (typeof val === "object") {
      if (seenObj.has(val)) return;
      seenObj.add(val);

      for (const [k, v] of Object.entries(val)) {
        const lower = String(k).toLowerCase();
        const looksLikeGroup = /(amenit|feature|extras?)/.test(lower);
        if (looksLikeGroup) {
          walk(v, depth + 1, seenObj);
          continue;
        }
        const truthy =
          v === true ||
          v === "true" ||
          v === 1 ||
          (typeof v === "string" && v.toLowerCase() === "yes");
        if (truthy) pushKey(k);
        else if (typeof v === "string") pushKey(v);
        else if (typeof v === "object") walk(v, depth + 1, seenObj);
      }
    }
  };

  walk(src);
  return out;
}

const normalizeAmenityKey = (v) => {
  try {
    const out = libNormalizeAmenityKey ? libNormalizeAmenityKey(v) : null;
    return out || localNormalizeAmenityKey(v);
  } catch {
    return localNormalizeAmenityKey(v);
  }
};

const toAmenityList = (v) => {
  try {
    const out = libToAmenityList ? libToAmenityList(v) : null;
    if (Array.isArray(out) && out.length) return out;
    return localToAmenityList(v);
  } catch {
    return localToAmenityList(v);
  }
};

function extractAmenitiesDeep(listing) {
  const out = [];
  const seen = new Set();

  const add = (items) => {
    for (const it of items || []) {
      const key = normalizeAmenityKey(it?.key || it?.label || it);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      out.push({
        key,
        label: AMENITY_CATALOG[key]?.label || it?.label || it?.key || key,
      });
    }
  };

  const candidates = [
    listing?.amenities,
    listing?.amenitiesSelected,
    listing?.selectedAmenities,
    listing?.features,
    listing?.amenityList,
    listing?.amenitiesList,
    listing?.details?.amenities,
    listing?.details,
    listing?.extra?.amenities,
    listing?.extra,
  ];

  for (const c of candidates) add(toAmenityList(c));
  if (out.length === 0) add(toAmenityList(listing));
  return out;
}

/* =========================================================================
   ICONS (Sage theme)
   ========================================================================= */
const IconChevronLeft = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M12.78 15.53a.75.75 0 01-1.06 0l-4.5-4.5a.75.75 0 010-1.06l4.5-4.5a.75.75 0 111.06 1.06L8.31 10l4.47 4.47a.75.75 0 010 1.06z"
      clipRule="evenodd"
    />
  </svg>
);

const IconChevronRight = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M7.22 4.47a.75.75 0 011.06 0l4.5 4.5a.75.75 0 010 1.06l-4.5 4.5a.75.75 0 11-1.06-1.06L11.69 10 7.22 5.53a.75.75 0 010-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

const IconX = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M4.22 4.22a.75.75 0 011.06 0L10 8.94l4.72-4.72a.75.75 0 111.06 1.06L11.06 10l4.72 4.72a.75.75 0 11-1.06 1.06L8.94 10 4.22 5.28a.75.75 0 010-1.06z"
      clipRule="evenodd"
    />
  </svg>
);

const IconShare = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
  </svg>
);

const IconHeart = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" />
  </svg>
);

const IconMapPin = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
      clipRule="evenodd"
    />
  </svg>
);

const IconUsers = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
  </svg>
);

const IconCalendar = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
      clipRule="evenodd"
    />
  </svg>
);

const IconCheck = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const IconHome = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
  </svg>
);

const IconClock = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
      clipRule="evenodd"
    />
  </svg>
);

const IconStar = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

/* --- NEW: minimal “brand” icons for badges + key facts (no emoji) --- */
const IconFast = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M5.5 6.5a1 1 0 011.6-.8l5.2 3.9a1 1 0 010 1.6l-5.2 3.9A1 1 0 015.5 14.3V6.5z" />
    <path d="M11.5 6.5a1 1 0 011.6-.8l3.8 2.85a1 1 0 010 1.6l-3.8 2.85a1 1 0 01-1.6-.8V6.5z" opacity=".65" />
  </svg>
);

const IconUserCheck = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M10 10a3.5 3.5 0 100-7 3.5 3.5 0 000 7zm-7 8a7 7 0 0114 0H3z"
      clipRule="evenodd"
      opacity=".35"
    />
    <path
      fillRule="evenodd"
      d="M16.707 10.293a1 1 0 010 1.414l-3.1 3.1a1 1 0 01-1.414 0l-1.55-1.55a1 1 0 011.414-1.414l.843.843 2.393-2.393a1 1 0 011.414 0z"
      clipRule="evenodd"
    />
  </svg>
);

const IconBadgeCheck = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.707a1 1 0 00-1.414-1.414L9 10.172 7.707 8.879A1 1 0 106.293 10.293l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

const IconBed = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M3 9a2 2 0 012-2h2a2 2 0 012 2v1H3V9z" opacity=".35" />
    <path d="M9 10h8a2 2 0 012 2v4a1 1 0 11-2 0v-1H3v1a1 1 0 11-2 0V7a1 1 0 112 0v3h6z" />
    <path d="M11 7h4a2 2 0 012 2v1h-6V9a2 2 0 010-2z" opacity=".35" />
  </svg>
);

const IconBath = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M6 7a3 3 0 116 0v3H6V7z" opacity=".35" />
    <path d="M4 10a1 1 0 000 2h.5A5.5 5.5 0 0010 17.5 5.5 5.5 0 0015.5 12H16a1 1 0 100-2H4z" />
    <path d="M13 6a1 1 0 112 0v2h-2V6z" opacity=".6" />
  </svg>
);

const IconParking = (props) => (
  <svg viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path
      fillRule="evenodd"
      d="M6 3a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2H6zm2 4a1 1 0 011-1h2.2a2.8 2.8 0 010 5.6H9v2.4a1 1 0 11-2 0V7zm3.2 3a.8.8 0 000-1.6H9v1.6h2.2z"
      clipRule="evenodd"
    />
  </svg>
);

/* =========================================================================
   UTILITIES
   ========================================================================= */
const FEE_PCT = 15;

const currency = (n) =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: "USD" }).format(
    n ?? 0
  );

const formatMoney = (n) =>
  n == null || isNaN(n) ? "—" : `$${Number(n).toLocaleString()}`;

const pad2 = (n) => String(n).padStart(2, "0");

const toYYYYMMDD = (d) => {
  try {
    return ymd(d);
  } catch {
    const dt = d instanceof Date ? d : new Date(d);
    return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
  }
};

function parseYYYYMMDD(s) {
  const [Y, M, D] = String(s || "").split("-").map(Number);
  if (!Y || !M || !D) return null;
  return new Date(Y, M - 1, D);
}

function addDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function todayYYYYMMDD() {
  return toYYYYMMDD(new Date());
}

function daysBetween(startStr, endStr) {
  const s = parseYYYYMMDD(startStr);
  const e = parseYYYYMMDD(endStr);
  if (!s || !e) return 0;
  const diff = Math.round(
    (e.setHours(0, 0, 0, 0) - s.setHours(0, 0, 0, 0)) / 86400000
  );
  return Math.max(0, diff);
}

function fmtHour(h) {
  const n = Number(h);
  if (!Number.isFinite(n) || n < 0 || n > 23) return "—";
  const ampm = n >= 12 ? "PM" : "AM";
  const hour = n % 12 || 12;
  return `${hour} ${ampm}`;
}

function normalizePriceCents(centsField, dollarsField) {
  if (Number.isFinite(Number(centsField)) && Number(centsField) > 0) {
    return Math.round(Number(centsField));
  }
  if (Number.isFinite(Number(dollarsField)) && Number(dollarsField) > 0) {
    return Math.round(Number(dollarsField) * 100);
  }
  return 0;
}

function toGuestPrice(baseDollars, precomputed) {
  const preset = Number(precomputed);
  if (Number.isFinite(preset) && preset > 0) return Math.round(preset);
  const b = Number(baseDollars);
  if (!Number.isFinite(b) || b <= 0) return null;
  return Math.round(b * (1 + FEE_PCT / 100));
}

/* =========================================================================
   AVAILABILITY NORMALIZATION (busySet)
   ========================================================================= */
function isBufferRow(r) {
  if (!r) return false;
  if (r.isBuffer === true) return true;
  if (r.buffer === true) return true;
  if (r.isPrep === true) return true;
  if (r.prep === true) return true;

  const t = String(r.type || r.reason || r.kind || r.category || "").toLowerCase();
  if (t.includes("buffer") || t.includes("prep") || t.includes("turnover")) return true;

  const s = String(r.status || "").toLowerCase();
  if (s.includes("buffer") || s.includes("prep") || s.includes("turnover") || s.includes("hold"))
    return true;

  const n = String(r.note || r.description || "").toLowerCase();
  if (
    n.includes("buffer") ||
    n.includes("prep") ||
    n.includes("turnover") ||
    n.includes("clean") ||
    n.includes("setup") ||
    n.includes("hold")
  )
    return true;

  return false;
}

function hasRowLevelBuffers(rows) {
  return (rows || []).some((r) => {
    const b = Number(r?.bufferBeforeDays ?? r?.bufferBefore ?? r?.bufferBeforeNights ?? 0);
    const a = Number(r?.bufferAfterDays ?? r?.bufferAfter ?? r?.bufferAfterNights ?? 0);
    return b > 0 || a > 0;
  });
}

function toBusyDateSetFromRows(rows) {
  const s = new Set();

  const normalizeDirect = (v) => {
    if (!v) return "";
    if (typeof v === "string") return v;
    if (v instanceof Date) return toYYYYMMDD(v);
    if (typeof v === "object" && typeof v.toDate === "function") {
      try {
        return toYYYYMMDD(v.toDate());
      } catch {}
    }
    return String(v);
  };

  const normalizeRangeDate = (v) => {
    if (!v) return null;
    if (typeof v === "string") return parseYYYYMMDD(v);
    if (v instanceof Date) return v;
    if (typeof v === "object" && typeof v.toDate === "function") {
      try {
        return v.toDate();
      } catch {}
    }
    return parseYYYYMMDD(String(v));
  };

  for (const r of rows || []) {
    if (!r) continue;

    const direct = r.date || r.day || r.ymd || r.blockedDate || r.bufferDate;
    if (direct) {
      const dd = normalizeDirect(direct);
      if (dd && /^\d{4}-\d{2}-\d{2}$/.test(dd)) s.add(dd);
      continue;
    }

    const start = r.start || r.startDate || r.from || r.checkIn || r.begin;
    const end = r.end || r.endDate || r.to || r.checkOut || r.finish;

    const bufBefore = Number(r.bufferBeforeDays ?? r.bufferBefore ?? r.bufferBeforeNights ?? 0);
    const bufAfter = Number(r.bufferAfterDays ?? r.bufferAfter ?? r.bufferAfterNights ?? 0);

    const sDate = normalizeRangeDate(start);
    const eDate = normalizeRangeDate(end);

    if (sDate && eDate) {
      let d = addDays(sDate, -Math.max(0, bufBefore));
      const stop = addDays(eDate, Math.max(0, bufAfter));
      while (d < stop) {
        s.add(toYYYYMMDD(d));
        d = addDays(d, 1);
      }
    }
  }

  return s;
}

/* =========================================================================
   CONSTANTS
   ========================================================================= */
const SPACE_TYPE_LABEL = {
  entire_place: "Entire place",
  private_room: "Private room",
  shared_room: "Shared room",
  outdoor_space: "Outdoor area",
  other: "Other",
};

const ENTRANCE_LABEL = {
  dedicated: "Dedicated entrance",
  shared: "Shared entrance",
};

/* =========================================================================
   CALENDAR PICKER (Same logic; fixed layout + no clipping)
   ========================================================================= */
function CalendarPicker({
  mode,
  date,
  startDate,
  endDate,
  onDateChange,
  onStartDateChange,
  onEndDateChange,
  busySet,
  minDate,
  className = "",
  isOpen = false,
  onClose = () => {},
}) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [nextMonth, setNextMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth() + 1, 1);
  });

  const navigateMonths = (direction) => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + direction, 1)
    );
    setNextMonth(new Date(nextMonth.getFullYear(), nextMonth.getMonth() + direction, 1));
  };

  const isDateDisabled = (d) => {
    const dateStr = toYYYYMMDD(d);
    return d < minDate || busySet.has(dateStr);
  };

  const isDateInRange = (d) => {
    if (!startDate || !endDate || mode !== "overnight") return false;
    const dateStr = toYYYYMMDD(d);
    return dateStr >= startDate && dateStr <= endDate;
  };

  const isDateSelected = (d) => {
    const dateStr = toYYYYMMDD(d);
    if (mode === "day") return dateStr === date;
    return dateStr === startDate || dateStr === endDate;
  };

  const handleDateClick = (d) => {
    const dateStr = toYYYYMMDD(d);
    if (isDateDisabled(d)) return;

    if (mode === "day") {
      onDateChange(dateStr);
      onClose();
      return;
    }

    // Overnight range selection (keep logic intact)
    if (!startDate || (startDate && endDate)) {
      onStartDateChange(dateStr);
      onEndDateChange("");
    } else if (startDate && !endDate) {
      if (dateStr <= startDate) {
        onEndDateChange(startDate);
        onStartDateChange(dateStr);
      } else {
        onEndDateChange(dateStr);
      }
      onClose();
    }
  };

  const renderMonth = (monthDate) => {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();

    const days = [];

    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = 0; i < startDay; i++) {
      const day = prevMonthLastDay - startDay + i + 1;
      const dateObj = new Date(year, month - 1, day);
      days.push({ date: dateObj, isCurrentMonth: false });
    }

    const daysInMonth = lastDay.getDate();
    for (let i = 1; i <= daysInMonth; i++) {
      const dateObj = new Date(year, month, i);
      days.push({ date: dateObj, isCurrentMonth: true });
    }

    const totalCells = 42;
    const remainingCells = totalCells - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      const dateObj = new Date(year, month + 1, i);
      days.push({ date: dateObj, isCurrentMonth: false });
    }

    const monthName = monthDate.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });

    return (
      <div className="w-[280px] flex-shrink-0">
        <div className="text-center font-medium text-gray-900 mb-3">{monthName}</div>

        <div className="grid grid-cols-7 gap-1 mb-1">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
            <div
              key={day}
              className="text-center text-xs text-gray-500 font-medium py-1"
            >
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map(({ date: dateObj, isCurrentMonth }, idx) => {
            const dateStr = toYYYYMMDD(dateObj);
            const disabled = isDateDisabled(dateObj);
            const selected = isDateSelected(dateObj);
            const inRange = isDateInRange(dateObj);
            const isToday = dateStr === todayYYYYMMDD();

            let btn =
              "h-9 w-9 flex items-center justify-center text-sm rounded-lg transition-colors";

            if (!isCurrentMonth) {
              btn += " text-gray-300";
            } else if (disabled) {
              btn += " text-gray-300 cursor-not-allowed bg-gray-50";
            } else if (selected) {
              btn += " bg-sage-600 text-white font-medium";
            } else if (inRange) {
              btn += " bg-sage-50";
            } else if (isToday) {
              btn += " border border-sage-600";
            } else {
              btn += " text-gray-800 hover:bg-sage-50";
            }

            return (
              <button
                key={idx}
                type="button"
                className={btn}
                onClick={() => handleDateClick(dateObj)}
                disabled={disabled || !isCurrentMonth}
              >
                {dateObj.getDate()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className={`absolute z-50 mt-2 bg-white rounded-xl border border-gray-200 shadow-lg p-4 ${className}`}
    >
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => navigateMonths(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <IconChevronLeft className="h-5 w-5" />
        </button>

        <button
          type="button"
          onClick={onClose}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          Close
        </button>

        <button
          type="button"
          onClick={() => navigateMonths(1)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          <IconChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Two months; scroll horizontally on narrow widths so months are fully visible */}
      <div className="flex gap-6 overflow-x-auto pb-2">
        {renderMonth(currentMonth)}
        {renderMonth(nextMonth)}
      </div>

      <div className="mt-4 pt-4 border-t border-gray-200 flex items-center justify-between">
        <button
          type="button"
          onClick={() => {
            if (mode === "day") onDateChange("");
            else {
              onStartDateChange("");
              onEndDateChange("");
            }
          }}
          className="text-sm text-gray-600 hover:text-gray-800"
        >
          Clear dates
        </button>
        <div className="text-xs text-gray-500">
          {mode === "day" ? "Select your date" : "Select check-in and check-out"}
        </div>
      </div>
    </div>
  );
}

/* =========================================================================
   SMALL UI PRIMITIVES (pills + stat cards)
   ========================================================================= */
function Pill({ icon: Icon, children, tone = "soft" }) {
  const base =
    "inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold border";
  const toneCls =
    tone === "solid"
      ? "bg-sage-600 text-white border-sage-600"
      : "bg-white text-sage-700 border-sage-200";
  return (
    <span className={`${base} ${toneCls}`}>
      {Icon ? <Icon className={`h-3.5 w-3.5 ${tone === "solid" ? "text-white" : "text-sage-700"}`} /> : null}
      <span className="leading-none">{children}</span>
    </span>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-gray-100 bg-white p-4 flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl bg-sage-50 border border-sage-100 flex items-center justify-center">
        <Icon className="h-5 w-5 text-sage-700" />
      </div>
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-sm font-semibold text-gray-900 truncate">{value}</div>
      </div>
    </div>
  );
}

/* =========================================================================
   AMENITY ITEM (FIX: always use local fallback icon)
   ========================================================================= */
function AmenityItem({ amenityKey, label }) {
  // LIB_AMENITY_ICONS intentionally null until your library provides icons.
  const LibIcon = LIB_AMENITY_ICONS?.[amenityKey] || LIB_AMENITY_ICONS?.[label];

  const FallbackIcon = IconCheck;
  const Icon = LibIcon || FallbackIcon;

  return (
    <div className="flex items-center gap-3 py-3 px-4 bg-white rounded-lg border border-gray-100 hover:border-sage-200 transition-colors">
      <div className="h-6 w-6 text-sage-600 flex items-center justify-center">
        <Icon className="h-4 w-4" />
      </div>
      <span className="text-gray-700 text-sm font-medium">{label}</span>
    </div>
  );
}

/* =========================================================================
   MODALS
   ========================================================================= */
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="p-2 text-gray-500 hover:text-gray-700">
            <IconX className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 overflow-y-auto max-h-[60vh]">{children}</div>
      </div>
    </div>
  );
}

function CancellationPolicyModal({ onClose }) {
  return (
    <Modal title="Cancellation Policy" onClose={onClose}>
      <div className="space-y-6 text-sm text-gray-800 leading-relaxed">
        <div className="font-semibold text-gray-900">
          You’re Welcome – Guest Cancellation &amp; Refund Policy
        </div>
        <div className="text-gray-700">Last updated: 12/11/2025</div>

        <div className="border-t border-gray-100 pt-4" />

        <div>
          <div className="font-semibold text-gray-900">1. What is refunded?</div>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
            <li>Refunds apply to the stay price (the amount paid for the space itself).</li>
            <li>
              The You’re Welcome service fee is non-refundable, except where required by law
              or if we make an exception under our Extenuating Circumstances policy.
            </li>
            <li>
              Any third-party costs (e.g., flights, catering, rentals) are never covered by
              this policy.
            </li>
          </ul>
        </div>

        <div>
          <div className="font-semibold text-gray-900">
            2. Before a booking is confirmed (request-to-book)
          </div>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
            <li>
              While your booking is still in “pending” / request-to-book status (not yet
              approved and not yet paid), you can cancel at any time.
            </li>
            <li>No charge is made and no refund calculation is needed.</li>
          </ul>
        </div>

        <div>
          <div className="font-semibold text-gray-900">3. After a booking is confirmed</div>
          <div className="mt-2 text-gray-700">
            Once your booking is confirmed and payment has been authorized/charged (the
            “booking time”), the rules below apply.
          </div>

          <div className="mt-3 bg-sage-50 border border-sage-100 rounded-xl p-4">
            <div className="font-semibold text-gray-900">
              A. 48-hour grace period (all bookings)
            </div>
            <div className="mt-2 text-gray-700">For all bookings, as long as:</div>
            <ol className="list-decimal pl-5 mt-2 space-y-1 text-gray-700">
              <li>You cancel within 48 hours of booking time, and</li>
              <li>Check-in is still more than 48 hours away,</li>
            </ol>
            <div className="mt-2 text-gray-700">
              → You receive a 100% refund of the stay price.
            </div>
            <div className="mt-1 text-gray-700">(Service fee is not refunded.)</div>
            <div className="mt-1 text-gray-700">
              If check-in is within 48 hours, the 48-hour grace period does not apply.
            </div>
          </div>
        </div>

        <div>
          <div className="font-semibold text-gray-900">
            4. Bookings made less than 60 days before check-in
          </div>
          <div className="mt-2 text-gray-700">
            If, at the time you booked, check-in was less than 60 days away:
          </div>
          <ul className="list-disc pl-5 mt-2 space-y-2 text-gray-700">
            <li>
              Within 48 hours of booking time
              <ul className="list-circle pl-5 mt-1 space-y-1">
                <li>
                  If check-in is still more than 48 hours away → 100% refund of the stay
                  price.
                </li>
              </ul>
            </li>
            <li>
              After 48 hours from booking time, or once you are within 48 hours of check-in
              <ul className="list-circle pl-5 mt-1 space-y-1">
                <li>No refund of the stay price if you cancel.</li>
              </ul>
            </li>
          </ul>
          <div className="mt-2 text-gray-700">
            After the 48-hour grace window ends, this is effectively a strict,
            non-refundable stay price.
          </div>
        </div>

        <div>
          <div className="font-semibold text-gray-900">
            5. Bookings made 60 days or more before check-in (far-in-advance bookings)
          </div>
          <div className="mt-2 text-gray-700">
            If, at the time you booked, check-in was 60 days or more away, you receive
            extra flexibility:
          </div>

          <ol className="list-decimal pl-5 mt-2 space-y-3 text-gray-700">
            <li>
              First 48 hours after booking time
              <ul className="list-circle pl-5 mt-1 space-y-1">
                <li>
                  Same as everyone else: if check-in is still &gt;48 hours away → 100% refund
                  of the stay price.
                </li>
              </ul>
            </li>
            <li>
              Extended full-refund window (additional 30 days)
              <ul className="list-circle pl-5 mt-1 space-y-1">
                <li>
                  From the end of the 48-hour grace period up to 30 days after booking
                  time, as long as your stay has not started:
                </li>
                <li>You may cancel and still receive a 100% refund of the stay price.</li>
              </ul>
            </li>
            <li>
              After the extended window ends
              <ul className="list-circle pl-5 mt-1 space-y-1">
                <li>Once both of these are true:</li>
              </ul>
              <ul className="list-none pl-5 mt-1 space-y-1 text-gray-700">
                <li> 48 hours have passed since booking time and</li>
                <li> The additional 30-day extended window has ended,</li>
              </ul>
              <ul className="list-circle pl-5 mt-2 space-y-1">
                <li>Your stay becomes non-refundable:</li>
              </ul>
              <ul className="list-none pl-5 mt-1 space-y-1 text-gray-700">
                <li> No refund of the stay price if you cancel.</li>
              </ul>
            </li>
          </ol>

          <div className="mt-2 text-gray-700">
            Service fees remain non-refundable in all cases, unless required by law or
            covered under Extenuating Circumstances.
          </div>
        </div>

        <div>
          <div className="font-semibold text-gray-900">6. After the stay has started</div>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
            <li>
              Once your check-in date/time has passed, you can no longer cancel via
              self-serve for a refund.
            </li>
            <li>
              Any requests at or after the start of the stay must go through You’re Welcome
              Support and will be handled under our Extenuating Circumstances and guest
              support guidelines.
            </li>
          </ul>
        </div>

        <div>
          <div className="font-semibold text-gray-900">7. How to cancel</div>
          <div className="mt-2 text-gray-700">To cancel your booking:</div>
          <ol className="list-decimal pl-5 mt-2 space-y-1 text-gray-700">
            <li>Go to Your Trips in your You’re Welcome account.</li>
            <li>Select the booking you want to cancel.</li>
            <li>Follow the on-screen steps.</li>
            <li>
              We will calculate your refund instantly based on:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Booking time (confirmedAt)</li>
                <li>Current time</li>
                <li>Time remaining until check-in</li>
                <li>Whether your booking qualifies for the extended window</li>
              </ul>
            </li>
          </ol>
          <div className="mt-2 text-gray-700">
            If you cannot access your account or encounter issues, contact
            support@yourewelcomeapp.com.
          </div>
        </div>

        <div>
          <div className="font-semibold text-gray-900">8. Extenuating Circumstances</div>
          <div className="mt-2 text-gray-700">
            We know life happens. In rare cases, you may face a situation that isn’t
            fairly covered by the standard rules above. These may include, for example:
          </div>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
            <li>Serious, unexpected medical emergencies affecting you or an immediate family member</li>
            <li>Government-issued travel restrictions or mandatory evacuations that make attendance impossible</li>
            <li>Significant natural disasters or severe weather events that impact the property or make it unsafe to travel</li>
            <li>Major utility failures at the listing (e.g., prolonged loss of power, water, or heat) that occur close to or during your stay</li>
            <li>Documented safety issues at the property that the host cannot reasonably resolve in time</li>
          </ul>

          <div className="mt-3 text-gray-700">In these situations:</div>
          <ol className="list-decimal pl-5 mt-2 space-y-1 text-gray-700">
            <li>
              Do not cancel solely through the app if you are outside the normal refund
              windows and believe your case qualifies as extenuating.
            </li>
            <li>
              Email support@yourewelcomeapp.com with:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Your booking ID</li>
                <li>Dates of your stay</li>
                <li>A short description of what happened</li>
                <li>Any documentation you can share (e.g., medical note, official notice, photos, news links)</li>
              </ul>
            </li>
          </ol>

          <div className="mt-3 text-gray-700">
            We will review your request and may, at our sole discretion, override the
            standard cancellation rules and:
          </div>
          <ul className="list-disc pl-5 mt-2 space-y-1 text-gray-700">
            <li>Provide a full or partial refund of the stay price,</li>
            <li>Offer a credit for a future booking, or</li>
            <li>Decline the request if the situation does not meet our extenuating criteria.</li>
          </ul>

          <div className="mt-3 text-xs text-gray-600">
            Decisions under the Extenuating Circumstances policy are case-by-case, final,
            and may consider the interests of both guests and hosts as well as local laws
            and regulations.
          </div>
        </div>
      </div>
    </Modal>
  );
}

/* =========================================================================
   REVIEWS (wired to show stars + comments robustly, like the original page)
   ========================================================================= */
function coerceReviewRating(r) {
  const raw =
    r?.rating ??
    r?.stars ??
    r?.starRating ??
    r?.score ??
    r?.overallRating ??
    r?.overall ??
    0;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(5, Math.round(n)));
}

function getReviewText(r) {
  const t = r?.text ?? r?.comment ?? r?.message ?? r?.body ?? r?.review ?? "";
  return String(t || "").trim();
}

function getReviewAuthorName(r) {
  const n = r?.authorName ?? r?.userName ?? r?.guestName ?? r?.name ?? "Guest";
  return String(n || "Guest").trim() || "Guest";
}

function getReviewWhen(r) {
  const ts =
    r?.createdAt?.seconds != null
      ? new Date(r.createdAt.seconds * 1000)
      : r?.createdAt
      ? new Date(r.createdAt)
      : r?.timestamp?.seconds != null
      ? new Date(r.timestamp.seconds * 1000)
      : r?.timestamp
      ? new Date(r.timestamp)
      : null;

  if (!ts || isNaN(ts.getTime())) return "";
  return ts.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function ReviewsSection({
  reviews = [],
  averageRating = null,
  totalReviews = 0,
  loading = false,
}) {
  if (loading) {
    return (
      <div className="mb-10">
        <div className="h-7 w-40 bg-gray-100 rounded animate-pulse mb-6" />
        <div className="space-y-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="border-b border-gray-100 pb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-gray-100 animate-pulse" />
                <div className="flex-1">
                  <div className="h-4 w-40 bg-gray-100 rounded animate-pulse mb-2" />
                  <div className="h-3 w-56 bg-gray-100 rounded animate-pulse" />
                </div>
              </div>
              <div className="h-4 w-full bg-gray-100 rounded animate-pulse mb-2" />
              <div className="h-4 w-5/6 bg-gray-100 rounded animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (!Array.isArray(reviews) || reviews.length === 0) return null;

  const avgOk = Number.isFinite(Number(averageRating)) && Number(averageRating) > 0;
  const countOk = Number.isFinite(Number(totalReviews)) && Number(totalReviews) > 0;

  return (
    <div className="mb-10">
      <h2 className="text-xl font-bold text-gray-900 mb-6">
        Reviews
        {avgOk && countOk && (
          <span className="ml-2 text-lg text-sage-700">
            ★ {Number(averageRating).toFixed(1)} · {totalReviews} review
            {totalReviews === 1 ? "" : "s"}
          </span>
        )}
      </h2>

      <div className="space-y-6">
        {reviews.map((review) => {
          const when = getReviewWhen(review);
          const rating = coerceReviewRating(review);
          const author = getReviewAuthorName(review);
          const text = getReviewText(review);

          return (
            <div key={review.id} className="border-b border-gray-100 pb-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-10 w-10 rounded-full bg-sage-100 flex items-center justify-center text-sage-700 font-medium">
                  {(author || "G").charAt(0)}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{author}</h4>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    {when && <span>{when}</span>}
                    {when && <span>·</span>}
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <IconStar
                          key={i}
                          className={`h-4 w-4 ${i < rating ? "text-amber-400" : "text-gray-300"}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {text ? (
                <p className="text-gray-700 whitespace-pre-wrap">{text}</p>
              ) : (
                <p className="text-gray-500 italic">No comment left.</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* =========================================================================
   MAIN PAGE
   ========================================================================= */
export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const loc = useLocation();
  const [search, setSearch] = useSearchParams();
  const auth = getAuth();

  const [listing, setListing] = useState(loc.state?.listing || null);
  const [loading, setLoading] = useState(!loc.state?.listing);
  const [error, setError] = useState("");

  const [mode, setMode] = useState("day");
  const [date, setDate] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [guests, setGuests] = useState(1);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef(null);

  const [busySet, setBusySet] = useState(() => new Set());
  const primaryRowsRef = useRef([]);
  const calendarRowsRef = useRef([]);

  const [bookError, setBookError] = useState("");

  const [liked, setLiked] = useState(false);
  const [sharedToast, setSharedToast] = useState(false);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const heroRef = useRef(null);
  const [heroIndex, setHeroIndex] = useState(0);

  const [rulesOpen, setRulesOpen] = useState(false);
  const [amenitiesOpen, setAmenitiesOpen] = useState(false);
  const [cancellationOpen, setCancellationOpen] = useState(false);

  const [hostProfile, setHostProfile] = useState(null);

  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  const sync = useCallback(
    (patch) => {
      const next = new URLSearchParams(search);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === "" || v == null) next.delete(k);
        else next.set(k, String(v));
      });
      setSearch(next, { replace: true });
    },
    [search, setSearch]
  );

  /* --------------------------------------------------------------------- */
  /*  Seed from URL params                                                 */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    const p = new URLSearchParams(loc.search || "");
    const m = (p.get("mode") || "").toLowerCase();
    const d = p.get("date");
    const s = p.get("start");
    const e = p.get("end");
    const g = Number(p.get("guests") || 0);

    if (m === "overnight") setMode("overnight");
    if (d) setDate(d);
    if (s) setStartDate(s);
    if (e) setEndDate(e);
    if (Number.isFinite(g) && g > 0) setGuests(g);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const minDate = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const bookingMode = (listing?.bookingMode || "").toLowerCase();
  const supportsOvernight =
    bookingMode === "overnight" ||
    bookingMode === "hybrid" ||
    Boolean(listing?.supportsOvernight) ||
    (Array.isArray(listing?.stayTypes) && listing.stayTypes.includes("overnight"));

  /* --------------------------------------------------------------------- */
  /*  Prices                                                               */
  /* --------------------------------------------------------------------- */
  const nightlyPriceCents = normalizePriceCents(
    listing?.nightlyPriceCents,
    listing?.nightlyPrice
  );
  const dayPriceCents = normalizePriceCents(
    listing?.dayPriceCents,
    listing?.dayPrice ?? listing?.dayRate
  );

  const baseDayPrice = dayPriceCents > 0 ? dayPriceCents / 100 : null;
  const baseNightPrice = nightlyPriceCents > 0 ? nightlyPriceCents / 100 : null;

  const guestDayPrice = toGuestPrice(baseDayPrice, listing?.guestDayPrice);
  const guestNightlyPrice = toGuestPrice(baseNightPrice, listing?.guestNightlyPrice);

  const hasGuestDayPrice = Number.isFinite(guestDayPrice) && Number(guestDayPrice) > 0;
  const hasGuestNightlyPrice =
    supportsOvernight &&
    Number.isFinite(guestNightlyPrice) &&
    Number(guestNightlyPrice) > 0;

  const minNights = Number.isFinite(Number(listing?.minNights))
    ? Number(listing?.minNights)
    : 1;
  const maxNights =
    Number.isFinite(Number(listing?.maxNights)) && Number(listing?.maxNights) > 0
      ? Number(listing?.maxNights)
      : 30;

  const checkInHour = Number.isFinite(Number(listing?.checkInHour))
    ? Number(listing?.checkInHour)
    : null;
  const checkOutHour = Number.isFinite(Number(listing?.checkOutHour))
    ? Number(listing?.checkOutHour)
    : null;

  const nights = useMemo(() => daysBetween(startDate, endDate), [startDate, endDate]);

  const rangeHasBusy = useMemo(() => {
    if (!startDate || !endDate) return false;
    let d = parseYYYYMMDD(startDate);
    const e = parseYYYYMMDD(endDate);
    while (d && e && d < e) {
      if (busySet.has(toYYYYMMDD(d))) return true;
      d = addDays(d, 1);
    }
    return false;
  }, [startDate, endDate, busySet]);

  const schemaPrice =
    (mode === "overnight" && hasGuestNightlyPrice
      ? guestNightlyPrice
      : hasGuestDayPrice
      ? guestDayPrice
      : hasGuestNightlyPrice
      ? guestNightlyPrice
      : baseDayPrice || baseNightPrice || 0) || 0;

  /* --------------------------------------------------------------------- */
  /*  Fetch/hydrate listing                                                */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    let mounted = true;

    (async () => {
      const hasListing = !!listing;
      const listId = listing?.id || id;
      const needsHydration = hasListing && listId;
      const shouldFetchFresh = !hasListing && !!id;

      if (!needsHydration && !shouldFetchFresh) {
        if (listing && supportsOvernight && bookingMode !== "day") {
          setMode((m) => (m === "day" ? "overnight" : m));
        }
        return;
      }

      try {
        if (shouldFetchFresh) setLoading(true);

        const mod = await import("../firebase");
        const getListingById = mod.getListingById || mod.fetchListing || mod.getListing;
        if (!getListingById) {
          setError("Listing loader not implemented.");
          return;
        }

        const docData = await getListingById(listId);
        if (!mounted) return;

        if (!docData) {
          setError("Listing not found.");
          return;
        }

        setListing((prev) => ({ ...(prev || {}), ...docData }));

        const bm = (docData?.bookingMode || "").toLowerCase();
        if (bm === "overnight") setMode((m) => (m === "day" ? "overnight" : m));
      } catch (e) {
        console.error(e);
        if (mounted) setError("Couldn't load this listing.");
      } finally {
        if (mounted && !listing) setLoading(false);
        if (mounted && shouldFetchFresh) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, listing?.id]);

  /* --------------------------------------------------------------------- */
  /*  Reviews                                                              */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    if (!listing?.id) return;

    const loadReviews = async () => {
      setReviewsLoading(true);
      try {
        const colRef = collection(db, "listings", listing.id, "reviews");
        const q1 = query(colRef, orderBy("createdAt", "desc"), limit(10));
        const snap1 = await getDocs(q1);

        if (!snap1.empty) {
          setReviews(
            snap1.docs.map((d) => ({
              id: d.id,
              ...d.data(),
            }))
          );
          return;
        }

        const topColRef = collection(db, "reviews");
        const q2 = query(
          topColRef,
          where("listingId", "==", listing.id),
          orderBy("createdAt", "desc"),
          limit(10)
        );
        const snap2 = await getDocs(q2);

        if (!snap2.empty) {
          setReviews(
            snap2.docs.map((d) => ({
              id: d.id,
              ...d.data(),
            }))
          );
        } else {
          setReviews([]);
        }
      } catch (e) {
        console.error("Error loading reviews:", e);
        setReviews([]);
      } finally {
        setReviewsLoading(false);
      }
    };

    loadReviews();
  }, [listing?.id]);

  /* --------------------------------------------------------------------- */
  /*  SEO title + JSON-LD                                                  */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    if (listing?.title && typeof document !== "undefined") {
      document.title = `${listing.title} • You're Welcome`;
    }
  }, [listing?.title]);

  useEffect(() => {
    if (!listing || typeof document === "undefined") return;

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: listing?.title || "Space",
      description: listing?.summary || "",
      image: Array.isArray(listing?.photos) ? listing.photos : [],
      offers: {
        "@type": "Offer",
        price: schemaPrice,
        priceCurrency: "USD",
        availability: "https://schema.org/InStock",
      },
    });

    document.head.appendChild(script);
    return () => {
      try {
        document.head.removeChild(script);
      } catch {}
    };
  }, [listing, schemaPrice]);

  /* --------------------------------------------------------------------- */
  /*  Availability subscription: today -> +6 months                         */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    if (!listing?.id) return;

    const from = todayYYYYMMDD();
    const to = ymd(addMonths(new Date(), 6));

    primaryRowsRef.current = [];
    calendarRowsRef.current = [];

    let unsubPrimary;
    let unsubCalendar;
    let alive = true;

    const deriveBusySet = () => {
      const primaryBusy = toBusyDateSetFromRows(primaryRowsRef.current);
      const calendarBusy = toBusyDateSetFromRows(calendarRowsRef.current);

      const merged = new Set([...primaryBusy, ...calendarBusy]);

      const rules = listing?.bookingRules || {};

      const before = Number(
        rules.prepDaysBefore ??
          rules.bufferDaysBefore ??
          rules.bufferBeforeDays ??
          listing?.prepDaysBefore ??
          listing?.bufferDaysBefore ??
          listing?.bufferBeforeDays ??
          0
      );

      const after = Number(
        rules.prepDaysAfter ??
          rules.bufferDaysAfter ??
          rules.bufferAfterDays ??
          listing?.prepDaysAfter ??
          listing?.bufferDaysAfter ??
          listing?.bufferAfterDays ??
          0
      );

      const turnover = Number(rules.turnoverDays ?? listing?.turnoverDays ?? 0);

      const finalBefore = before > 0 ? before : turnover > 0 ? turnover : 0;
      const finalAfter = after > 0 ? after : turnover > 0 ? turnover : 0;

      const primaryBufferRows = (primaryRowsRef.current || []).filter(isBufferRow);
      const calendarBufferRows = (calendarRowsRef.current || []).filter(isBufferRow);
      const hasRowLevelBuffersFlag = hasRowLevelBuffers(primaryRowsRef.current);

      const serverBuffersLikelyPresent =
        primaryBufferRows.length > 0 || calendarBufferRows.length > 0 || hasRowLevelBuffersFlag;

      if ((finalBefore > 0 || finalAfter > 0) && !serverBuffersLikelyPresent) {
        const baseRows = (primaryRowsRef.current || []).filter((r) => !isBufferRow(r));
        const baseSet = toBusyDateSetFromRows(baseRows);

        for (const ds of Array.from(baseSet)) {
          const d = parseYYYYMMDD(ds);
          if (!d) continue;

          for (let i = 1; i <= finalBefore; i++) merged.add(toYYYYMMDD(addDays(d, -i)));
          for (let i = 1; i <= finalAfter; i++) merged.add(toYYYYMMDD(addDays(d, +i)));
        }
      }

      if (!alive) return;
      setBusySet(merged);
    };

    unsubPrimary = watchAvailabilityRange({
      listingId: listing.id,
      fromYYYYMMDD: from,
      toYYYYMMDD: to,
      onChange: (rows) => {
        primaryRowsRef.current = rows || [];
        deriveBusySet();
      },
      onError: (err) => console.error(err),
    });

    try {
      const calRef = collection(db, "listings", listing.id, "calendar");
      const qCal = query(calRef, orderBy(documentId()), startAt(from), endAt(to));

      unsubCalendar = onSnapshot(
        qCal,
        (snap) => {
          const rows = snap.docs.map((d) => ({
            id: d.id,
            date: d.id,
            ...(d.data() || {}),
          }));
          calendarRowsRef.current = rows || [];
          deriveBusySet();
        },
        (err) => console.error(err)
      );
    } catch (e) {
      calendarRowsRef.current = [];
      deriveBusySet();
    }

    deriveBusySet();

    return () => {
      alive = false;
      if (typeof unsubPrimary === "function") unsubPrimary();
      if (typeof unsubCalendar === "function") unsubCalendar();
    };
  }, [listing?.id]);

  /* --------------------------------------------------------------------- */
  /*  Click-outside calendar                                                */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    function handleClickOutside(event) {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setCalendarOpen(false);
      }
    }

    if (calendarOpen) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [calendarOpen]);

  /* --------------------------------------------------------------------- */
  /*  Listing derived                                                       */
  /* --------------------------------------------------------------------- */
  const title = listing?.title || "Beautiful space";
  const city = listing?.city || "—";
  const stateCode = listing?.stateCode || listing?.state || "—";
  const summary =
    listing?.summary ||
    "A tasteful space perfect for gatherings, workshops, and celebrations.";

  // EDIT: House rules — do NOT invent rules. If blank, show a placeholder in UI.
  const rulesRaw = listing?.rules || "";
  const rules = rulesRaw
    ? rulesRaw
        .split("\n")
        .map((r) => r.trim())
        .filter(Boolean)
    : [];

  const capacityRaw = Number(listing?.capacity);
  const capacity = Number.isFinite(capacityRaw) && capacityRaw > 0 ? capacityRaw : null;

  const photos = Array.isArray(listing?.photos)
    ? listing.photos.filter(Boolean)
    : [
        "https://images.unsplash.com/photo-1505692794403-34d4982f88aa?q=80&w=1920&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1494526585095-c41746248156?q=80&w=1920&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1505691938895-1758d7feb511?q=80&w=1920&auto=format&fit=crop",
        "https://images.unsplash.com/photo-1519710164239-da123dc03ef4?q=80&w=1920&auto=format&fit=crop",
      ];

  const spaceType = listing?.spaceType || "entire_place";
  const spaceName = (listing?.spaceName || "").trim();
  const entranceType = listing?.entranceType || "shared";

  const amenityList = useMemo(() => {
    const sources = [
      listing?.amenities,
      listing?.amenitiesSelected,
      listing?.selectedAmenities,
      listing?.features,
      listing?.amenityList,
      listing?.amenitiesList,
      listing?.details?.amenities,
      listing?.extra?.amenities,
    ];

    const seen = new Set();
    const merged = [];

    const tryAdd = (src) => {
      if (!src) return;
      const raw = toAmenityList(src) || [];
      for (const it of raw) {
        const key = normalizeAmenityKey(it?.key || it?.label || it);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push({
          key,
          label: AMENITY_CATALOG[key]?.label || it?.label || it?.key || key,
        });
      }
    };

    for (const src of sources) tryAdd(src);

    if (merged.length === 0) {
      const deep = extractAmenitiesDeep(listing) || [];
      for (const it of deep) {
        const key = normalizeAmenityKey(it?.key || it?.label || it);
        if (!key || seen.has(key)) continue;
        seen.add(key);
        merged.push({
          key,
          label: AMENITY_CATALOG[key]?.label || it?.label || it?.key || key,
        });
      }
    }

    const orderIndex = new Map(AMENITY_ORDER.map((k, i) => [k, i]));
    merged.sort((a, b) => {
      const ai = orderIndex.has(a.key) ? orderIndex.get(a.key) : Infinity;
      const bi = orderIndex.has(b.key) ? orderIndex.get(b.key) : Infinity;
      if (ai !== bi) return ai - bi;
      return (a.label || a.key).localeCompare(b.label || b.key);
    });

    return merged;
  }, [listing]);

  const topAmenities = amenityList.slice(0, 6);

  const hasSmokeAlarm = amenityList.some((a) => a.key === "smoke alarm");
  const hasCarbonMonoxideAlarm = amenityList.some((a) => a.key === "carbon monoxide alarm");
  const hasFireExtinguisher = amenityList.some((a) => a.key === "fire extinguisher");

  const hostUid =
    listing?.hostUid ||
    listing?.ownerUid ||
    listing?.uid ||
    listing?.userId ||
    listing?.user?.uid;

  const me = auth.currentUser;
  const isViewerHost = !!(me && hostUid && me.uid === hostUid);

  /* --------------------------------------------------------------------- */
  /*  EDIT: instant vs request-to-book derived from approvalWindowHours     */
  /*  Rule: if approvalWindowHours exists (and >0) => host approval required
           else => instant
   * --------------------------------------------------------------------- */
  const approvalWindowHours = useMemo(() => {
    const raw = listing?.approvalWindowHours;
    const n = Number(raw);
    if (raw == null) return null;
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  }, [listing?.approvalWindowHours]);

  const isRequestToBook = approvalWindowHours != null;
  const isInstantBook = !isRequestToBook;

  /* --------------------------------------------------------------------- */
  /*  EDIT: key facts (beds/baths/bedrooms/parking)                         */
  /* --------------------------------------------------------------------- */
  const bedrooms = useMemo(() => {
    const n = Number(listing?.bedrooms);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [listing?.bedrooms]);

  const beds = useMemo(() => {
    const n = Number(listing?.beds);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [listing?.beds]);

  const bathrooms = useMemo(() => {
    const n = Number(listing?.bathrooms);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [listing?.bathrooms]);

  const parkingLabel = useMemo(() => {
    const direct =
      listing?.parkingType ||
      listing?.parking ||
      listing?.parkingLabel ||
      listing?.parkingInfo ||
      null;

    if (typeof direct === "string" && direct.trim()) return direct.trim();

    const hasParkingAmenity = amenityList.some((a) => {
      const k = String(a?.key || "").toLowerCase();
      const l = String(a?.label || "").toLowerCase();
      return k.includes("parking") || l.includes("parking") || k.includes("garage") || l.includes("garage");
    });

    if (hasParkingAmenity) return "Parking available";
    return "Not specified";
  }, [listing, amenityList]);

  /* --------------------------------------------------------------------- */
  /*  Hydrate host profile                                                 */
  /* --------------------------------------------------------------------- */
  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!hostUid) {
        if (mounted) setHostProfile(null);
        return;
      }

      try {
        let profile = null;

        try {
          const mod = await import("../firebase");
          const candidates = [
            mod.getUserProfile,
            mod.getProfileByUid,
            mod.getHostByUid,
            mod.fetchUser,
            mod.fetchUserProfile,
          ].filter(Boolean);

          for (const fn of candidates) {
            try {
              const res = await fn(hostUid);
              if (res) {
                profile = res;
                break;
              }
            } catch {}
          }
        } catch {}

        if (!profile) {
          const tryCol = async (colName) => {
            try {
              const ref = doc(db, colName, hostUid);
              const snap = await getDoc(ref);
              if (snap.exists()) return { id: snap.id, ...snap.data() };
            } catch {}
            return null;
          };

          profile =
            (await tryCol("users")) || (await tryCol("profiles")) || (await tryCol("hosts"));
        }

        if (!profile) {
          const u = auth.currentUser;
          if (u && u.uid === hostUid) {
            profile = {
              uid: u.uid,
              displayName: u.displayName || "Host",
              photoURL: u.photoURL || null,
              email: u.email || null,
            };
          }
        }

        if (mounted) setHostProfile(profile || null);
      } catch {
        if (mounted) setHostProfile(null);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [hostUid, auth]);

  const baseHost = listing?.host || listing?.user || {};
  const mergedHost = { ...baseHost, ...(hostProfile || {}) };

  // EDIT: Do NOT show host last name — always first name only (or first token of displayName)
  const hostFirstName = (() => {
    const first = String(mergedHost?.firstName || "").trim();
    if (first) return first;

    const display = String(mergedHost?.displayName || mergedHost?.name || "").trim();
    if (display) return display.split(/\s+/)[0];

    const email = String(mergedHost?.email || "").trim();
    if (email && email.includes("@")) {
      const local = email.split("@")[0];
      const token = local.split(/[._-]/)[0];
      if (token) return token.charAt(0).toUpperCase() + token.slice(1);
    }

    return "Host";
  })();

  const hostAvatar =
    mergedHost?.photoURL ||
    mergedHost?.avatarUrl ||
    mergedHost?.photo ||
    mergedHost?.profilePhotoUrl ||
    null;

  // EDIT: Do NOT show property address (street) — show ballpark only.
  const showLocationSection = Boolean(listing?.city || listing?.stateCode || listing?.state);
  const approxAreaLabel = useMemo(() => {
    const zip =
      listing?.addressPostalCode ||
      listing?.postalCode ||
      listing?.zip ||
      listing?.zipCode ||
      "";
    const base = `${city}, ${stateCode}`;
    if (zip && String(zip).trim()) return `Near ${base} ${String(zip).trim()}`;
    return `Near ${base}`;
  }, [listing, city, stateCode]);

  // Rating: prefer listing aggregates; fall back to computed from loaded reviews
  const listingAvg = listing?.ratingAvg;
  const listingCount = Number(listing?.reviewCount || 0) || 0;

  const computedFromReviews = useMemo(() => {
    if (!Array.isArray(reviews) || reviews.length === 0) return { avg: null, count: 0 };
    const nums = reviews.map((r) => {
      const raw =
        r?.rating ?? r?.stars ?? r?.starRating ?? r?.score ?? r?.overallRating ?? r?.overall ?? 0;
      const n = Number(raw);
      return Number.isFinite(n) ? Math.max(0, Math.min(5, n)) : 0;
    });
    const count = nums.filter((n) => n > 0).length || reviews.length;
    const sum = nums.reduce((a, b) => a + b, 0);
    const avg = count > 0 ? sum / nums.length : null;
    return { avg, count: reviews.length };
  }, [reviews]);

  const averageRating =
    Number.isFinite(Number(listingAvg)) && Number(listingAvg) > 0
      ? Number(listingAvg)
      : Number.isFinite(Number(computedFromReviews.avg)) && Number(computedFromReviews.avg) > 0
      ? Number(computedFromReviews.avg)
      : null;

  const totalReviews = listingCount > 0 ? listingCount : computedFromReviews.count;

  const activeGuestUnitPrice =
    mode === "overnight"
      ? hasGuestNightlyPrice
        ? guestNightlyPrice
        : hasGuestDayPrice
        ? guestDayPrice
        : null
      : hasGuestDayPrice
      ? guestDayPrice
      : hasGuestNightlyPrice
      ? guestNightlyPrice
      : null;

  const quantityForSubtotal = mode === "overnight" ? (nights > 0 ? nights : 0) : date ? 1 : 0;
  const estimatedSubtotal =
    activeGuestUnitPrice && quantityForSubtotal > 0
      ? activeGuestUnitPrice * quantityForSubtotal
      : null;

  const isBookDisabled =
    (mode === "day" && (!date || guests < 1 || busySet.has(date))) ||
    (mode === "overnight" &&
      (!startDate ||
        !endDate ||
        guests < 1 ||
        nights <= 0 ||
        nights < minNights ||
        nights > maxNights ||
        rangeHasBusy));

  /* --------------------------------------------------------------------- */
  /*  Handlers                                                             */
  /* --------------------------------------------------------------------- */
  const onBook = useCallback(() => {
    setBookError("");

    if (mode === "overnight") {
      if (!startDate || !endDate) return setBookError("Select check-in and check-out.");
      if (nights <= 0) return setBookError("Check-out must be after check-in.");
      if (nights < minNights)
        return setBookError(`Minimum stay is ${minNights} night${minNights === 1 ? "" : "s"}.`);
      if (nights > maxNights) return setBookError(`Maximum stay is ${maxNights} nights.`);
      if (rangeHasBusy)
        return setBookError("Those dates include an unavailable day—please choose a different range.");
      if (guests < 1) return setBookError("Guests must be at least 1.");

      navigate(
        `/checkout/${listing?.id || id}?mode=overnight&start=${encodeURIComponent(
          startDate
        )}&end=${encodeURIComponent(endDate)}&guests=${guests}`,
        { state: { listing } }
      );
      return;
    }

    if (!date) return setBookError("Please pick a date.");
    if (busySet.has(date)) return setBookError("That date is unavailable—please choose another.");
    if (guests < 1) return setBookError("Guests must be at least 1.");

    navigate(
      `/checkout/${listing?.id || id}?mode=day&date=${encodeURIComponent(date)}&guests=${encodeURIComponent(
        guests
      )}`,
      { state: { listing } }
    );
  }, [
    mode,
    date,
    startDate,
    endDate,
    nights,
    minNights,
    maxNights,
    rangeHasBusy,
    guests,
    listing,
    id,
    busySet,
    navigate,
  ]);

  const onMessageHost = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        navigate(
          `/account?returnTo=${encodeURIComponent(
            window.location.pathname + window.location.search
          )}`
        );
        return;
      }
      if (hostUid && user.uid === hostUid) return;

      if (!listing?.id || !hostUid) {
        alert("Missing listing or host info.");
        return;
      }

      const thread = await createOrGetInquiryThread({
        listingId: listing.id,
        hostUid,
        guestUid: user.uid,
        listingTitle: listing.title,
        listingPhoto: listing.photos?.[0],
      });

      navigate(`/messages?thread=${thread.id}`);
    } catch (e) {
      console.error(e);
      alert("Could not start the conversation. Please try again.");
    }
  }, [auth, hostUid, listing, navigate]);

  const onShare = useCallback(async () => {
    try {
      if (navigator.share) await navigator.share({ title, url: window.location.href });
      else await navigator.clipboard.writeText(window.location.href);

      setSharedToast(true);
      setTimeout(() => setSharedToast(false), 1200);
    } catch {}
  }, [title]);

  const onToggleLike = useCallback(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate(
        `/account?returnTo=${encodeURIComponent(
          window.location.pathname + window.location.search
        )}`
      );
      return;
    }
    setLiked((v) => !v);
  }, [auth, navigate]);

  const openLightbox = useCallback(
    (index = 0) => {
      if (!photos?.length) return;
      const clamped = Math.max(0, Math.min(index, photos.length - 1));
      setLightboxIndex(clamped);
      setLightboxOpen(true);
      if (typeof document !== "undefined") document.body.style.overflow = "hidden";
    },
    [photos?.length]
  );

  const closeLightbox = useCallback(() => {
    setLightboxOpen(false);
    if (typeof document !== "undefined") document.body.style.overflow = "";
  }, []);

  const nextImage = useCallback(
    () => setLightboxIndex((i) => (i + 1) % Math.max(1, photos.length)),
    [photos.length]
  );
  const prevImage = useCallback(
    () =>
      setLightboxIndex((i) => (i - 1 + Math.max(1, photos.length)) % Math.max(1, photos.length)),
    [photos.length]
  );

  useEffect(() => {
    if (!lightboxOpen) return;
    function onKey(e) {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") nextImage();
      if (e.key === "ArrowLeft") prevImage();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, closeLightbox, nextImage, prevImage]);

  const onHeroScroll = useCallback(() => {
    const el = heroRef.current;
    if (!el) return;
    const { scrollLeft, clientWidth } = el;
    const i = Math.round(scrollLeft / Math.max(1, clientWidth));
    setHeroIndex(Math.min(Math.max(i, 0), photos.length - 1));
  }, [photos.length]);

  const formatDateForDisplay = (dateStr) => {
    if (!dateStr) return "Add date";
    const d = parseYYYYMMDD(dateStr);
    return d ? d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "Add date";
  };

  /* --------------------------------------------------------------------- */
  /*  Loading / Error                                                      */
  /* --------------------------------------------------------------------- */
  if (loading) {
    return (
      <main className="min-h-screen bg-white">
        <div className="mx-auto max-w-7xl px-4 py-8">
          <div className="animate-pulse space-y-8">
            <div className="h-96 w-full rounded-xl bg-gray-100" />
            <div className="grid gap-8 lg:grid-cols-3">
              <div className="lg:col-span-2 space-y-4">
                <div className="h-8 w-2/3 bg-gray-100 rounded" />
                <div className="h-4 w-1/3 bg-gray-100 rounded" />
                <div className="h-32 bg-gray-100 rounded" />
              </div>
              <div className="h-96 bg-gray-100 rounded-xl" />
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (error || !listing) {
    return (
      <main className="min-h-screen bg-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="text-4xl mb-4">🏠</div>
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Space not found</h1>
          <p className="text-gray-600 mb-6">
            {error || "This listing may have been removed or is no longer available."}
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate(-1)}
              className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Go back
            </button>
            <Link
              to="/explore"
              className="px-4 py-2 bg-sage-600 text-white rounded-lg hover:bg-sage-700"
            >
              Explore spaces
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <>
      <style>{`
        /* Sticky card */
        @media (min-width: 1024px) {
          .booking-card-sticky { position: sticky; top: 1.5rem; }
        }

        /* HARD FIX: If your Tailwind build isn't generating the custom "sage" palette,
           these CSS fallbacks ensure the buttons + UI are never invisible. */
        :root{
          --yw-sage:#7C9082;
          --yw-sage-hover:#6C7F72;
          --yw-sage-50:#F3F6F4;
          --yw-sage-100:#E7EEE9;
          --yw-sage-200: rgba(124,144,130,0.25);
          --yw-sage-300: rgba(124,144,130,0.35);
          --yw-sage-100b: rgba(124,144,130,0.15);
        }
        .bg-sage-600{ background-color: var(--yw-sage) !important; }
        .bg-sage-700{ background-color: var(--yw-sage-hover) !important; }
        .hover\\:bg-sage-700:hover{ background-color: var(--yw-sage-hover) !important; }
        .bg-sage-50{ background-color: var(--yw-sage-50) !important; }
        .bg-sage-100{ background-color: var(--yw-sage-100) !important; }

        .text-sage-600{ color: var(--yw-sage) !important; }
        .text-sage-700{ color: var(--yw-sage) !important; }
        .hover\\:text-sage-800:hover{ color: #5E6F64 !important; }

        .border-sage-100{ border-color: var(--yw-sage-100b) !important; }
        .border-sage-200{ border-color: var(--yw-sage-200) !important; }
        .border-sage-300{ border-color: var(--yw-sage-300) !important; }

        .focus\\:ring-sage-500:focus{ --tw-ring-color: var(--yw-sage) !important; }
      `}</style>

      <main className="min-h-screen bg-white">
        {/* Sticky Header */}
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-100">
          <div className="mx-auto max-w-7xl px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-gray-700 hover:text-gray-900"
              >
                <IconChevronLeft className="h-5 w-5" />
                <span className="text-sm font-medium">Back</span>
              </button>

              <div className="flex items-center gap-3">
                <button
                  onClick={onShare}
                  className="p-2 text-gray-600 hover:text-gray-800"
                  aria-label="Share"
                >
                  <IconShare className="h-5 w-5" />
                </button>

                <button
                  onClick={onToggleLike}
                  className="p-2 text-gray-600 hover:text-gray-800"
                  aria-label={liked ? "Saved" : "Save"}
                >
                  <IconHeart className={`h-5 w-5 ${liked ? "text-red-500 fill-red-500" : ""}`} />
                </button>
              </div>
            </div>

            {sharedToast && (
              <div className="mt-2 text-xs text-sage-700 bg-sage-50 border border-sage-100 inline-flex px-3 py-1 rounded-full">
                Link copied
              </div>
            )}
          </div>
        </div>

        {/* Photos - Mobile Carousel (unchanged behavior) */}
        <div className="lg:hidden relative">
          <div
            ref={heroRef}
            onScroll={onHeroScroll}
            className="flex snap-x snap-mandatory overflow-x-auto scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {photos.map((src, i) => (
              <button
                key={i}
                type="button"
                onClick={() => openLightbox(i)}
                className="w-full flex-shrink-0 snap-center"
              >
                <img src={src} alt={`${title} ${i + 1}`} className="w-full h-[60vh] object-cover" />
              </button>
            ))}
          </div>

          {photos.length > 1 && (
            <div className="absolute bottom-4 right-4 bg-black/70 rounded-full px-3 py-1.5 text-xs text-white">
              {heroIndex + 1} / {photos.length}
            </div>
          )}
        </div>

        {/* Photos - Desktop (EDIT: centered hero + thumbnails below; not Airbnb grid) */}
        <div className="hidden lg:block">
          <div className="mx-auto max-w-7xl px-4 py-8">
            <div className="mx-auto max-w-5xl">
              <button type="button" onClick={() => openLightbox(0)} className="block w-full">
                <img
                  src={photos[0]}
                  alt={title}
                  className="w-full h-[520px] object-cover rounded-2xl"
                />
              </button>

              {photos.length > 1 && (
                <div className="mt-4 flex items-center justify-between gap-4">
                  <div className="flex-1 flex gap-3 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    {photos.slice(1).map((src, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => openLightbox(i + 1)}
                        className="flex-shrink-0"
                        aria-label={`Open photo ${i + 2}`}
                      >
                        <img
                          src={src}
                          alt={`${title} ${i + 2}`}
                          className="h-24 w-36 object-cover rounded-xl border border-gray-100 hover:border-sage-200 transition-colors"
                        />
                      </button>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => openLightbox(0)}
                    className="hidden xl:inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-sage-200 text-sage-700 hover:bg-sage-50 text-sm font-semibold"
                  >
                    View all
                    <IconChevronRight className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="mx-auto max-w-7xl px-4 py-6">
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            {/* Left: Details */}
            <div className="lg:col-span-2">
              {/* Title */}
              <div className="mb-8">
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
                  <span className="font-medium">{spaceName || title}</span>
                  <span>·</span>
                  <span>{SPACE_TYPE_LABEL[spaceType] || "Space"}</span>
                  {entranceType === "dedicated" && (
                    <>
                      <span>·</span>
                      <span>{ENTRANCE_LABEL[entranceType]}</span>
                    </>
                  )}
                </div>

                <h1 className="text-3xl font-bold text-gray-900 mb-4">{title}</h1>

                {/* EDIT: booking/verified badges (minimal icons, brand color) */}
                <div className="flex flex-wrap items-center gap-2 mb-5">
                  <Pill icon={isInstantBook ? IconFast : IconUserCheck}>
                    {isInstantBook
                      ? "Instant confirmation"
                      : `Host approval · within ${approvalWindowHours}h`}
                  </Pill>

                  {/* EDIT: verified badge on ALL listings */}
                  <Pill icon={IconBadgeCheck}>Verified host</Pill>

                  {/* Day-use clarity */}
                  {mode === "day" && (
                    <Pill icon={IconCheck}>Day use · full-day access</Pill>
                  )}
                  {mode !== "day" && (
                    <Pill icon={IconCheck}>Overnight</Pill>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-3 mb-6">
                  <div className="flex items-center gap-2 text-gray-700">
                    <IconMapPin className="h-5 w-5 text-gray-500" />
                    <span className="font-medium">{approxAreaLabel}</span>
                  </div>

                  {capacity != null && (
                    <>
                      <div className="w-px h-4 bg-gray-300" />
                      <div className="flex items-center gap-2 text-gray-700">
                        <IconUsers className="h-5 w-5 text-gray-500" />
                        <span>Up to {capacity} guests</span>
                      </div>
                    </>
                  )}

                  {supportsOvernight && (
                    <>
                      <div className="w-px h-4 bg-gray-300" />
                      <div className="text-gray-700">Overnight available</div>
                    </>
                  )}

                  {Number.isFinite(Number(averageRating)) && totalReviews > 0 && (
                    <>
                      <div className="w-px h-4 bg-gray-300" />
                      <div className="flex items-center gap-1 text-gray-700">
                        <IconStar className="h-5 w-5 text-amber-400" />
                        <span>
                          {Number(averageRating).toFixed(1)} · {totalReviews} review
                          {totalReviews === 1 ? "" : "s"}
                        </span>
                      </div>
                    </>
                  )}
                </div>

                {/* EDIT: key facts row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <StatCard
                    icon={IconHome}
                    label="Bedrooms"
                    value={bedrooms != null ? String(bedrooms) : "—"}
                  />
                  <StatCard
                    icon={IconBed}
                    label="Beds"
                    value={beds != null ? String(beds) : "—"}
                  />
                  <StatCard
                    icon={IconBath}
                    label="Bathrooms"
                    value={bathrooms != null ? String(bathrooms) : "—"}
                  />
                  <StatCard icon={IconParking} label="Parking" value={parkingLabel} />
                </div>

                <div className="inline-flex items-baseline gap-2 bg-sage-50 px-4 py-3 rounded-xl">
                  <span className="text-2xl font-bold text-sage-700">
                    {activeGuestUnitPrice ? formatMoney(activeGuestUnitPrice) : "Price upon request"}
                  </span>
                  <span className="text-sage-600">/ {mode === "overnight" ? "night" : "day"}</span>
                </div>
              </div>

              {/* About */}
              <div className="mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-4">About this space</h2>
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{summary}</p>
              </div>

              {/* Things to know */}
              <div className="mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Things to know</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-sage-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-8 w-8 bg-sage-100 rounded-lg flex items-center justify-center">
                        <IconHome className="h-5 w-5 text-sage-600" />
                      </div>
                      <h3 className="font-semibold text-gray-900">House rules</h3>
                    </div>

                    {rules.length > 0 ? (
                      <>
                        <ul className="space-y-2">
                          {rules.slice(0, 3).map((rule, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                              <IconCheck className="h-4 w-4 text-sage-600 mt-0.5 flex-shrink-0" />
                              <span>{rule}</span>
                            </li>
                          ))}
                        </ul>

                        {rules.length > 3 && (
                          <button
                            onClick={() => setRulesOpen(true)}
                            className="mt-3 text-sage-600 hover:text-sage-800 font-medium text-sm"
                          >
                            Show all rules
                          </button>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-gray-700">
                        House rules not provided.{" "}
                        {!isViewerHost && (
                          <button
                            onClick={onMessageHost}
                            className="text-sage-600 hover:text-sage-800 font-semibold"
                          >
                            Message the host
                          </button>
                        )}{" "}
                        with any questions.
                      </div>
                    )}
                  </div>

                  <div className="bg-sage-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-8 w-8 bg-sage-100 rounded-lg flex items-center justify-center">
                        <IconCheck className="h-5 w-5 text-sage-600" />
                      </div>
                      <h3 className="font-semibold text-gray-900">Safety & property</h3>
                    </div>

                    <ul className="space-y-2 text-sm text-gray-700">
                      {hasSmokeAlarm && (
                        <li className="flex items-center gap-2">
                          <IconCheck className="h-4 w-4 text-sage-600" />
                          Smoke alarm
                        </li>
                      )}
                      {hasCarbonMonoxideAlarm && (
                        <li className="flex items-center gap-2">
                          <IconCheck className="h-4 w-4 text-sage-600" />
                          Carbon monoxide alarm
                        </li>
                      )}
                      {hasFireExtinguisher && (
                        <li className="flex items-center gap-2">
                          <IconCheck className="h-4 w-4 text-sage-600" />
                          Fire extinguisher
                        </li>
                      )}
                      {!hasSmokeAlarm && !hasCarbonMonoxideAlarm && !hasFireExtinguisher && (
                        <li className="text-gray-600">No safety info provided.</li>
                      )}
                    </ul>
                  </div>

                  <div className="bg-sage-50 rounded-xl p-6">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="h-8 w-8 bg-sage-100 rounded-lg flex items-center justify-center">
                        <IconClock className="h-5 w-5 text-sage-600" />
                      </div>
                      <h3 className="font-semibold text-gray-900">Cancellation policy</h3>
                    </div>

                    <p className="text-sm text-gray-700 mb-3">
                      Refund rules depend on booking time and how soon check-in is.
                    </p>

                    <button
                      onClick={() => setCancellationOpen(true)}
                      className="text-sage-600 hover:text-sage-800 font-medium text-sm"
                    >
                      Show policy details
                    </button>
                  </div>
                </div>
              </div>

              {/* Amenities */}
              {amenityList.length > 0 && (
                <div className="mb-10">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-gray-900">What this place offers</h2>
                    {amenityList.length > 6 && (
                      <button
                        onClick={() => setAmenitiesOpen(true)}
                        className="text-sage-600 hover:text-sage-800 font-medium text-sm"
                      >
                        Show all ({amenityList.length})
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {topAmenities.map(({ key, label }) => (
                      <AmenityItem key={key} amenityKey={key} label={label} />
                    ))}
                  </div>
                </div>
              )}

              {/* Reviews (stars + comments) */}
              <ReviewsSection
                reviews={reviews}
                averageRating={averageRating}
                totalReviews={totalReviews}
                loading={reviewsLoading}
              />

              {/* Space details */}
              <div className="mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Space details</h2>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Type</div>
                    <div className="font-medium text-gray-900">
                      {SPACE_TYPE_LABEL[spaceType] || "Space"}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Entrance</div>
                    <div className="font-medium text-gray-900">
                      {ENTRANCE_LABEL[entranceType] || "—"}
                    </div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Bedrooms</div>
                    <div className="font-medium text-gray-900">{bedrooms != null ? bedrooms : "—"}</div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Beds</div>
                    <div className="font-medium text-gray-900">{beds != null ? beds : "—"}</div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Bathrooms</div>
                    <div className="font-medium text-gray-900">{bathrooms != null ? bathrooms : "—"}</div>
                  </div>

                  <div className="space-y-1">
                    <div className="text-sm text-gray-500">Parking</div>
                    <div className="font-medium text-gray-900">{parkingLabel}</div>
                  </div>

                  {capacity != null && (
                    <div className="space-y-1">
                      <div className="text-sm text-gray-500">Max guests</div>
                      <div className="font-medium text-gray-900">{capacity}</div>
                    </div>
                  )}

                  {supportsOvernight && (
                    <div className="space-y-1">
                      <div className="text-sm text-gray-500">Minimum stay</div>
                      <div className="font-medium text-gray-900">
                        {minNights} {minNights === 1 ? "night" : "nights"}
                      </div>
                    </div>
                  )}

                  {supportsOvernight && (
                    <>
                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Check-in</div>
                        <div className="font-medium text-gray-900">
                          {checkInHour != null ? fmtHour(checkInHour) : "Flexible"}
                        </div>
                      </div>

                      <div className="space-y-1">
                        <div className="text-sm text-gray-500">Check-out</div>
                        <div className="font-medium text-gray-900">
                          {checkOutHour != null ? fmtHour(checkOutHour) : "Flexible"}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Host (no last name) */}
              <div className="mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Your host</h2>
                <div className="bg-white rounded-xl border border-gray-100 p-6">
                  <div className="flex items-start gap-4">
                    <div className="h-20 w-20 rounded-full overflow-hidden bg-gray-100">
                      {hostAvatar ? (
                        <img src={hostAvatar} alt={hostFirstName} className="h-full w-full object-cover" />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center text-gray-400">
                          <IconHome className="h-10 w-10" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <div className="font-bold text-gray-900 text-lg">
                          Hosted by {hostFirstName}
                        </div>
                        {/* Verified badge always */}
                        <Pill icon={IconBadgeCheck}>Verified</Pill>
                      </div>

                      {listing?.hostNote && (
                        <p className="text-gray-600 mb-4 text-sm leading-relaxed">
                          {listing.hostNote}
                        </p>
                      )}

                      {!isViewerHost && (
                        <button
                          onClick={onMessageHost}
                          className="px-5 py-2.5 border border-sage-300 text-sage-700 rounded-lg hover:bg-sage-50 font-medium text-sm"
                        >
                          Message host
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Location (no street address, ballpark only) */}
              {showLocationSection && (
                <div className="mb-10">
                  <h2 className="text-xl font-bold text-gray-900 mb-6">Location</h2>
                  <div className="bg-white rounded-xl border border-gray-100 p-6">
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-xl bg-sage-50 border border-sage-100 flex items-center justify-center flex-shrink-0">
                        <IconMapPin className="h-5 w-5 text-sage-700" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{approxAreaLabel}</div>
                        <div className="text-gray-600 text-sm mt-1">
                          Exact address is shared after booking.
                        </div>
                      </div>
                    </div>

                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                        `${city} ${stateCode}`
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sage-600 hover:text-sage-800 font-medium text-sm mt-4"
                    >
                      <span>View approximate area</span>
                      <IconChevronRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Right: Booking Card */}
            <div className="lg:col-span-1" ref={calendarRef}>
              {/* FIX: overflow-visible so calendar popover never clips */}
              <div className="booking-card-sticky rounded-2xl border border-gray-200 bg-white shadow-sm overflow-visible">
                <div className="p-6 border-b border-gray-100 bg-gray-50">
                  <div className="flex items-baseline gap-2">
                    <span className="text-3xl font-bold text-sage-700">
                      {activeGuestUnitPrice ? formatMoney(activeGuestUnitPrice) : "—"}
                    </span>
                    <span className="text-gray-600">/ {mode === "overnight" ? "night" : "day"}</span>
                  </div>

                  {/* EDIT: day-use clarity */}
                  <div className="text-sm text-gray-600 mt-2">
                    {supportsOvernight ? (
                      "Day or overnight stays"
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-sage-600" />
                        Day use · full-day access (no overnight)
                      </span>
                    )}
                  </div>

                  {/* EDIT: booking/verified pills inside card (subtle) */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Pill icon={isInstantBook ? IconFast : IconUserCheck}>
                      {isInstantBook ? "Instant" : `Approval · ${approvalWindowHours}h`}
                    </Pill>
                    <Pill icon={IconBadgeCheck}>Verified host</Pill>
                  </div>
                </div>

                <div className="p-6">
                  {(bookingMode === "hybrid" || (supportsOvernight && hasGuestDayPrice)) && (
                    <div className="flex border border-gray-300 rounded-lg mb-6 overflow-hidden">
                      <button
                        type="button"
                        className={`flex-1 py-3 text-sm font-medium text-center ${
                          mode === "day" ? "bg-sage-600 text-white" : "text-gray-700 hover:bg-gray-50"
                        }`}
                        onClick={() => {
                          setMode("day");
                          setBookError("");
                          sync({ mode: "day", start: "", end: "" });
                        }}
                      >
                        Day use
                      </button>

                      <button
                        type="button"
                        className={`flex-1 py-3 text-sm font-medium text-center ${
                          mode === "overnight"
                            ? "bg-sage-600 text-white"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                        onClick={() => {
                          setMode("overnight");
                          setBookError("");
                          sync({ mode: "overnight", date: "" });
                        }}
                      >
                        Overnight
                      </button>
                    </div>
                  )}

                  {/* Date Selection (anchored popover via relative wrapper) */}
                  <div className="relative">
                    {mode === "day" ? (
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-3">
                          Select date
                        </label>

                        <button
                          type="button"
                          onClick={() => setCalendarOpen(true)}
                          className="w-full p-4 border border-gray-300 rounded-lg text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-sage-500 focus:border-transparent"
                        >
                          <div className="flex items-center justify-between">
                            <span className={date ? "text-gray-900 font-medium" : "text-gray-500"}>
                              {date ? formatDateForDisplay(date) : "Choose a date"}
                            </span>
                            <IconCalendar className="h-5 w-5 text-gray-400" />
                          </div>
                        </button>
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-4 mb-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Check-in
                          </label>
                          <button
                            type="button"
                            onClick={() => setCalendarOpen(true)}
                            className="w-full p-4 border border-gray-300 rounded-lg text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-sage-500 focus:border-transparent"
                          >
                            <div className="flex items-center justify-between">
                              <span
                                className={startDate ? "text-gray-900 font-medium" : "text-gray-500"}
                              >
                                {startDate ? formatDateForDisplay(startDate) : "Add date"}
                              </span>
                              <IconCalendar className="h-5 w-5 text-gray-400" />
                            </div>
                          </button>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">
                            Check-out
                          </label>
                          <button
                            type="button"
                            onClick={() => setCalendarOpen(true)}
                            className="w-full p-4 border border-gray-300 rounded-lg text-left hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-sage-500 focus:border-transparent"
                          >
                            <div className="flex items-center justify-between">
                              <span
                                className={endDate ? "text-gray-900 font-medium" : "text-gray-500"}
                              >
                                {endDate ? formatDateForDisplay(endDate) : "Add date"}
                              </span>
                              <IconCalendar className="h-5 w-5 text-gray-400" />
                            </div>
                          </button>
                        </div>
                      </div>
                    )}

                    <CalendarPicker
                      mode={mode}
                      date={date}
                      startDate={startDate}
                      endDate={endDate}
                      onDateChange={(newDate) => {
                        setDate(newDate);
                        setBookError("");
                        sync({ mode: "day", date: newDate, start: "", end: "" });
                      }}
                      onStartDateChange={(newStartDate) => {
                        setStartDate(newStartDate);
                        setBookError("");
                        sync({ mode: "overnight", start: newStartDate, end: endDate, date: "" });
                      }}
                      onEndDateChange={(newEndDate) => {
                        setEndDate(newEndDate);
                        setBookError("");
                        sync({ mode: "overnight", start: startDate, end: newEndDate, date: "" });
                      }}
                      busySet={busySet}
                      minDate={minDate}
                      isOpen={calendarOpen}
                      onClose={() => setCalendarOpen(false)}
                      className="left-0 right-0 w-full max-w-full"
                    />
                  </div>

                  {/* Guests */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Guests
                    </label>

                    <div className="flex items-center border border-gray-300 rounded-lg">
                      <button
                        type="button"
                        onClick={() => guests > 1 && setGuests(guests - 1)}
                        className="p-4 text-gray-600 hover:text-gray-800 disabled:text-gray-300"
                        disabled={guests <= 1}
                      >
                        <span className="text-xl">−</span>
                      </button>

                      <div className="flex-1 text-center">
                        <div className="text-gray-900 font-medium text-lg">{guests}</div>
                        <div className="text-xs text-gray-500">{guests === 1 ? "guest" : "guests"}</div>
                      </div>

                      <button
                        type="button"
                        onClick={() => {
                          const cap = capacity ?? 50;
                          if (guests < cap) setGuests(guests + 1);
                        }}
                        className="p-4 text-gray-600 hover:text-gray-800 disabled:text-gray-300"
                        disabled={capacity != null ? guests >= capacity : false}
                      >
                        <span className="text-xl">+</span>
                      </button>
                    </div>
                  </div>

                  {bookError && (
                    <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                      {bookError}
                    </div>
                  )}

                  {estimatedSubtotal && (
                    <div className="mb-6 border-t border-gray-100 pt-6">
                      <div className="space-y-3">
                        <div className="flex justify-between text-gray-700">
                          <span>
                            {formatMoney(activeGuestUnitPrice)} × {quantityForSubtotal}{" "}
                            {mode === "overnight" ? "night" : "day"}
                            {quantityForSubtotal !== 1 ? "s" : ""}
                          </span>
                          <span className="font-medium">{currency(estimatedSubtotal)}</span>
                        </div>
                        <div className="flex justify-between text-gray-700 border-t border-gray-100 pt-3">
                          <span className="font-medium">Total</span>
                          <span className="font-bold text-lg">{currency(estimatedSubtotal)}</span>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-3">Additional fees and taxes may apply</div>
                    </div>
                  )}

                  {/* EDIT: Button label depends on approvalWindowHours */}
                  <button
                    onClick={onBook}
                    disabled={isBookDisabled}
                    className="w-full py-4 bg-sage-600 text-white font-bold rounded-lg hover:bg-sage-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-lg"
                  >
                    {isBookDisabled
                      ? "Select dates to continue"
                      : isInstantBook
                      ? "Book now"
                      : "Request to book"}
                  </button>

                  {/* EDIT: What happens next (conditional) */}
                  <div className="mt-4 rounded-xl border border-sage-100 bg-sage-50 p-4">
                    <div className="text-xs font-bold text-sage-700 mb-2">
                      What happens next
                    </div>
                    {isInstantBook ? (
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <IconCheck className="h-4 w-4 text-sage-600 mt-0.5" />
                          Your booking is confirmed instantly.
                        </li>
                        <li className="flex items-start gap-2">
                          <IconCheck className="h-4 w-4 text-sage-600 mt-0.5" />
                          Exact address is shared after booking.
                        </li>
                        <li className="flex items-start gap-2">
                          <IconCheck className="h-4 w-4 text-sage-600 mt-0.5" />
                          Day use is full-day access (no overnight).
                        </li>
                      </ul>
                    ) : (
                      <ul className="space-y-2 text-sm text-gray-700">
                        <li className="flex items-start gap-2">
                          <IconCheck className="h-4 w-4 text-sage-600 mt-0.5" />
                          You’re sending a request to the host.
                        </li>
                        <li className="flex items-start gap-2">
                          <IconCheck className="h-4 w-4 text-sage-600 mt-0.5" />
                          Host responds within {approvalWindowHours} hours.
                        </li>
                        <li className="flex items-start gap-2">
                          <IconCheck className="h-4 w-4 text-sage-600 mt-0.5" />
                          Exact address is shared after booking.
                        </li>
                      </ul>
                    )}
                  </div>

                  <div className="mt-6 text-center text-sm text-gray-500">
                    <button
                      onClick={() => setCancellationOpen(true)}
                      className="text-sage-600 hover:text-sage-800 font-medium"
                    >
                      View cancellation policy
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Modals */}
      {rulesOpen && (
        <Modal title="House rules" onClose={() => setRulesOpen(false)}>
          {rules.length > 0 ? (
            <div className="space-y-4">
              {rules.map((rule, i) => (
                <div key={i} className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <IconCheck className="h-5 w-5 text-sage-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-800">{rule}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-700">
              House rules not provided.{" "}
              {!isViewerHost && (
                <button onClick={onMessageHost} className="text-sage-600 hover:text-sage-800 font-semibold">
                  Message the host
                </button>
              )}{" "}
              with any questions.
            </div>
          )}
        </Modal>
      )}

      {amenitiesOpen && (
        <Modal title="All amenities" onClose={() => setAmenitiesOpen(false)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {amenityList.map(({ key, label }) => (
              <AmenityItem key={key} amenityKey={key} label={label} />
            ))}
          </div>
        </Modal>
      )}

      {cancellationOpen && <CancellationPolicyModal onClose={() => setCancellationOpen(false)} />}

      {/* Lightbox */}
      {lightboxOpen && photos.length > 0 && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <div className="flex items-center justify-between p-6 text-white">
            <button onClick={closeLightbox} className="p-3 hover:bg-white/10 rounded-full">
              <IconX className="h-6 w-6" />
            </button>
            <div className="text-sm">
              {lightboxIndex + 1} / {photos.length}
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center p-4">
            <img
              src={photos[lightboxIndex]}
              alt={`${title} ${lightboxIndex + 1}`}
              className="max-h-full max-w-full object-contain"
            />
          </div>

          {photos.length > 1 && (
            <div className="absolute inset-0 flex items-center justify-between p-6 pointer-events-none">
              <button
                onClick={prevImage}
                className="pointer-events-auto p-3 text-white hover:bg-white/10 rounded-full"
              >
                <IconChevronLeft className="h-8 w-8" />
              </button>
              <button
                onClick={nextImage}
                className="pointer-events-auto p-3 text-white hover:bg-white/10 rounded-full"
              >
                <IconChevronRight className="h-8 w-8" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mobile Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 lg:hidden shadow-lg">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-bold text-sage-700 text-lg truncate">
              {activeGuestUnitPrice ? formatMoney(activeGuestUnitPrice) : "—"}
              <span className="text-sm font-normal text-gray-600">
                {" "}
                / {mode === "overnight" ? "night" : "day"}
              </span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              {isInstantBook ? "Instant confirmation" : `Host approval · ${approvalWindowHours}h`}
            </div>
          </div>

          <button
            onClick={onBook}
            disabled={isBookDisabled}
            className="px-6 py-3 bg-sage-600 text-white font-bold rounded-lg disabled:opacity-50 text-base whitespace-nowrap"
          >
            {isInstantBook ? "Book now" : "Request"}
          </button>
        </div>
      </div>
    </>
  );
}
